import { apiClient, ApiResponse } from "./client";

export interface TenantInfo {
    organizationId: number;
    name: string;
    displayName: string | null;
    organizationType: string;  // company, academy, learning_center 등
    subdomain: string;
    logoUrl: string | null;
    primaryColor: string | null;
    contractEndDate: string | null;
    languageMode: string;  // ko, en, both
}

export interface License {
    id: number;
    organizationId: number;
    productId: number;
    productName: string | null;
    curriculumContentId: number | null; // 과정 배정에 사용되는 콘텐츠 ID
    curriculumTitle: string | null; // 커리큘럼 제목
    validFrom: string;
    validUntil: string | null;
    isActive: boolean;
    createdAt: string;
    purchasedSeats: number; // 구매한 좌석 수 (0이면 무제한)
    usedSeats: number; // 현재 사용 중인 좌석 수
    allowSelfRegistration: boolean; // 학습자 자유 등록 허용
    allowAdminAssignment: boolean; // Admin 할당 허용
}

// 구매한 상품 (실제 구독)
export interface OrganizationSubscription {
    id: number;
    productId: number;
    productName: string | null;
    curriculumContentId: number | null;
    curriculumTitle: string | null;
    status: string;
    purchasedSeats: number; // 구매한 좌석 수 (0이면 무제한)
    usedSeats: number; // 현재 사용 중인 좌석 수
    validFrom: string;
    validUntil: string | null;
    createdAt: string;
    isBundle?: boolean;  // 번들 상품 여부
    bundleCurriculumTitles?: string[] | null;  // 번들에 포함된 커리큘럼 이름들
}

export interface LicenseSettingsRequest {
    allowSelfRegistration: boolean;
    allowAdminAssignment: boolean;
}

// ============================================================================
// Company Settings (Company Admin 수정 가능)
// ============================================================================

export interface CompanySettings {
    // 퀴즈 설정
    preQuizCount?: number | null;          // Pre-Quiz 문제 수 (null = 전체)
    postQuizCount?: number | null;         // Post-Quiz 문제 수 (null = 전체)
    quizPassThreshold?: number;            // 퀴즈 통과 기준 (%)
    quizRetryLimit?: number;               // 퀴즈 재시도 제한 (0 = 무제한)
    
    // 영상 설정
    videoCompletionThreshold?: number;     // 영상 수료 기준 (%)
    allowVideoSkip?: boolean;              // 영상 건너뛰기 허용
    maxPlaybackSpeed?: number;             // 최대 배속 (1.0 = 배속 불가)
    defaultPlaybackSpeed?: number;         // 기본 재생 속도
    
    // 수료 설정
    essayRequired?: boolean;               // 에세이 필수 여부
    completionDeadlineDays?: number | null; // 기본 수강 기한 (일)
    certificateAutoIssue?: boolean;        // 수료증 자동 발급
    certificateTemplate?: string;          // 수료증 템플릿
    
    // UI/브랜딩
    welcomeMessage?: string | null;        // 커스텀 환영 메시지
    announcementBanner?: string | null;    // 공지 배너 텍스트
    showQuebonLogo?: boolean;              // Quebon 로고 표시
    customFooterText?: string | null;      // 커스텀 푸터 문구
    
    // 알림 설정
    sendCompletionEmail?: boolean;         // 수료 시 이메일 발송
    sendReminderEmail?: boolean;           // 리마인더 이메일 발송
    reminderDaysBefore?: number;           // 마감 N일 전 리마인더
}

export interface CompanySettingsResponse {
    organizationId: number;
    companySettings: Record<string, unknown>;
    quizPassThreshold: number;
    videoCompletionThreshold: number;
    essayRequired: boolean;
    welcomeMessage: string | null;
    certificateAutoIssue: boolean;
}

export type CompanySettingsRequest = Partial<CompanySettings>;

export const tenantApi = {
    getCurrentTenant: () =>
        apiClient.get<ApiResponse<TenantInfo | null>>("/b2b/tenant"),

    getTenantBySubdomain: (subdomain: string) =>
        apiClient.get<ApiResponse<TenantInfo | null>>(
            `/b2b/tenant/${subdomain}`,
            { params: { _t: Date.now() } } // 캐시 버스팅
        ),

    // 구매 가능한 상품 목록 조회 (Open to Org로 공개된 것)
    getLicenses: () =>
        apiClient.get<ApiResponse<License[]>>("/b2b/tenant/licenses"),

    // 모든 활성 라이선스 목록 조회 (구매 가능 + 구매한 것 모두)
    getAllActiveLicenses: () =>
        apiClient.get<ApiResponse<License[]>>("/b2b/tenant/licenses/all"),

    // 구매한 상품(구독) 목록 조회
    getSubscriptions: () =>
        apiClient.get<ApiResponse<OrganizationSubscription[]>>(
            "/b2b/tenant/subscriptions"
        ),

    // 라이선스 설정 업데이트
    updateLicenseSettings: (
        licenseId: number,
        settings: LicenseSettingsRequest
    ) =>
        apiClient.put<ApiResponse<License>>(
            `/b2b/tenant/licenses/${licenseId}/settings`,
            settings
        ),

    // 좌석수 확장
    expandSeats: (licenseId: number, additionalSeats: number) =>
        apiClient.post<ApiResponse<License>>(
            `/b2b/tenant/licenses/${licenseId}/expand-seats`,
            { additionalSeats }
        ),

    // ============================================================================
    // Company Settings (Company Admin 수정 가능)
    // ============================================================================

    // 회사 설정 조회
    getCompanySettings: () =>
        apiClient.get<ApiResponse<CompanySettingsResponse>>("/b2b/tenant/settings"),

    // 회사 설정 수정
    updateCompanySettings: (settings: CompanySettingsRequest) =>
        apiClient.put<ApiResponse<CompanySettingsResponse>>(
            "/b2b/tenant/settings",
            settings
        ),

    // ============================================================================
    // Subscription 삭제 (Company Admin 전용)
    // ============================================================================

    // 구독(구매한 상품) 삭제
    deleteSubscription: (subscriptionId: number, force: boolean = false) =>
        apiClient.delete<ApiResponse<{ deleted: boolean; subscriptionId: number }>>(
            `/b2b/tenant/subscriptions/${subscriptionId}`,
            { params: { force } }
        ),

    // 구독을 사용하는 클래스 수 조회
    getSubscriptionClassCount: (subscriptionId: number) =>
        apiClient.get<ApiResponse<{ count: number }>>(
            `/b2b/tenant/subscriptions/${subscriptionId}/class-count`
        ),
};
